/**
 * H3 hexagon layer for GPS jamming visualization.
 * Renders flat H3 res-4 hex cells colored by interference level — WM's
 * crisp tessellating grid (DeckGLMap.ts:3708-3725), replacing the soft
 * overlapping severity circles that shipped first.
 */

import { H3HexagonLayer } from '@deck.gl/geo-layers';
import type { Layer } from '@deck.gl/core';
import type { NormalizedEvent } from '@/sources/base-source';

/** Layer IDs managed by this renderer. */
export const H3_JAMMING_LAYER_IDS = new Set(['gps-jamming']);

/** A jamming hex ready for rendering. */
export interface JamHex {
  h3Index: string;
  level: 'high' | 'medium';
}

/** Fill colors by level — WM DeckGLMap.ts:3713-3716. */
const FILL_COLOR: Record<JamHex['level'], [number, number, number, number]> = {
  high: [255, 80, 80, 180],
  medium: [255, 180, 50, 140],
};

/**
 * Map gpsjam events to hexes using WM's level cutoffs
 * (scripts/_gpsjam-parse.mjs:54-59): pct = bad/total * 100, cells with
 * fewer than 3 aircraft dropped, pct > 10 -> high, pct >= 2 -> medium.
 * The event population itself keeps GPSJam's own (bad-1)/total >= 2%
 * noise filter from the source — a deliberate deviation: it drops all
 * single-bad-aircraft cells (WM can show 1/9 as high) and bad=2 cells
 * in high-traffic hexes that WM would show as medium.
 */
export function eventsToJamHexes(events: NormalizedEvent[]): JamHex[] {
  const hexes: JamHex[] = [];
  for (const e of events) {
    const raw = e.raw as Record<string, unknown> | undefined;
    const h3Index = raw?.h3;
    const countBad = raw?.countBad;
    const total = raw?.totalAircraft;
    if (typeof h3Index !== 'string' || typeof countBad !== 'number' || typeof total !== 'number') continue;
    if (total < 3) continue;

    const pct = (countBad / total) * 100;
    if (pct > 10) hexes.push({ h3Index, level: 'high' });
    else if (pct >= 2) hexes.push({ h3Index, level: 'medium' });
  }
  return hexes;
}

/**
 * Create the H3 hexagon layer for GPS jamming.
 * No zoom opacity — WM renders at full strength at every zoom.
 */
export function createH3JammingLayer(hexes: JamHex[]): Layer[] {
  if (hexes.length === 0) return [];

  return [
    new H3HexagonLayer<JamHex>({
      id: 'gps-jamming-h3',
      data: hexes,
      pickable: true,
      filled: true,
      stroked: true,
      extruded: false,
      getHexagon: (d: JamHex) => d.h3Index,
      getFillColor: (d: JamHex) => FILL_COLOR[d.level],
      getLineColor: [255, 255, 255, 80],
      getLineWidth: 1,
      lineWidthUnits: 'pixels' as const,
      updateTriggers: {
        getFillColor: [hexes.length],
      },
    }) as unknown as Layer,
  ];
}
